/// <amd-module name="Reporting/Visuals/Scalar" />
import React = require("react");
import { VisualTitle } from "Reporting/Visuals/VisualTitle";
import { ColorUtilities } from "VSS/Features/Charts/Internal/ColorUtilities";
import { FPSLink, IFPSLinkProps } from "VSS/Features/PlatformUI/FPSLink";
import { VssComponent } from "VSS/Platform/Layout";

export interface ScalarProps {
    targetUrl?: string;
    targetUrlShouldUseNewTab?: boolean;
    disableFPS?: boolean;
    onClick?: () => void;
    title: string;
    value: number;
    /**
     * The largest number to display. If the value prop exceeds this number then the visualization will show
     * this number with a plus sign next to it. For example, if value is 100, but maxValue
     * is 99, then the visual will display "99+".
     */
    maxDisplayValue?: number;
    subtitle: string;
    backgroundColor: string;
}

/**
 * Visualizes a single integer value.
 *
 * The text color will automatically adjust to ensure contrast with the background.
 *
 * The text font size will automatically adjust to ensure the value fits visually. In extreme cases, the value will be elegantly truncated.
 */
export class Scalar extends VssComponent<ScalarProps, {}> {
    public render(): JSX.Element {
        return (
            <div className="reporting-visuals-common-scalar" style={{ backgroundColor: this.props.backgroundColor }}>
                {this.renderLink(this.renderContent())}
            </div>
        );
    }

    private renderCount(): JSX.Element {
        const fontSizes: { [numberOfDigits: number]: number } = {
            1: 72,
            2: 72,
            3: 72,
            4: 60,
            5: 50,
            6: 40,
            7: 30
        };

        // Restrict displayed value if exceeds maxValue
        const valueToDisplay =
            this.props.maxDisplayValue && this.props.value > this.props.maxDisplayValue ? `${this.props.maxDisplayValue}+` : this.props.value;

        const numberOfDigits = valueToDisplay.toString().length;
        let fontSize = fontSizes[numberOfDigits];
        if (!fontSize) {
            fontSize = 25;
        }

        return (
            <p className="count" style={{ fontSize: `${fontSize}px` }}>
                {valueToDisplay}
            </p>
        );
    }

    private renderContent(): JSX.Element {
        return (
            <div className="content" style={{ color: ColorUtilities.selectForegroundColor(this.props.backgroundColor) }}>
                <VisualTitle text={this.props.title} suppressDefaultPadding />
                {this.renderCount()}
                <p className="subtitle">{this.props.subtitle}</p>
            </div>
        );
    }

    private renderLink(content: JSX.Element): JSX.Element {
        if (!this.props.targetUrl) {
            return content;
        }
        const linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement> & IFPSLinkProps = {
            href: this.props.targetUrl,
            className: "link"
        };
        if (this.props.onClick) {
            linkProps.onClick = this.props.onClick;
        }
        if (this.props.targetUrlShouldUseNewTab) {
            linkProps.target = "_blank";
            linkProps.rel = "noreferrer noopener nofollow";
            linkProps.supportsFPS = false;
        }
        if(this.props.disableFPS) {
            linkProps.supportsFPS = false;
        }

        return <FPSLink {...linkProps} removeUnderline={true}>{content}</FPSLink>;
    }
}
